import styled, { css } from '@emotion/styled';
import {
  Container,
  Autocomplete,
  TextField,
  ButtonGroup,
  Box,
  Stack,
  List,
  ListItem,
  ListItemButton,
  Dialog,
  DialogTitle,
  IconButton,
  Button,
} from '@mui/material';
import { shouldForwardProp } from '@/utils/shouldForwardProp';
import { Link } from 'react-router-dom';
import { theme } from '@components/Styles/theme';

// ===============================================
// 로그인 화면 스타일 컴포넌트
// ===============================================
export const LoginContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export const LoginBox = styled(Box)`
  width: 400px;
  box-sizing: border-box;

  & a {
    position: relative;
    font-size: 14px;
    color: #767676;
    text-decoration: none;
    cursor: pointer;

    // &:first-of-type {
    //   margin-right: 9px;

    //   &::after {
    //     content: '';
    //     display: inline-block;
    //     position: absolute;
    //     top: 4px;
    //     right: -5px;
    //     width: 1px;
    //     height: 8px;
    //     background-color: #d9d9d9;
    //   }
    // }
  }
`;

export const LoginTextField = styled(TextField)`
  width: 100%;
  margin-bottom: 8px;

  & .MuiInputBase-root {
    line-height: 1;
    border-radius: 8px;
  }

  & input {
    font-size: 16px;

    &::placeholder {
      color: #767676;
    }
  }
`;

// ===============================================
// 컨테이너 스타일 컴포넌트
// ===============================================
export const CustomContainer = styled(Container)`
  padding: 32px 24px;
  background-color: #f2f2f2;
  width: 100%;
  min-height: 100vh;
  height: 100%;
  display: inline-table;

  @media (max-width: 1280px) {
    width: 1200px;
    overflow-x: scroll;
  }

  @media (max-width: 600px) {
    padding: 24px 16px;
  }
`;

// ===============================================
// 텍스트필드 스타일 컴포넌트
// ===============================================
export const CustomAutocomplete = styled(Autocomplete, { shouldForwardProp })`
  width: ${({ width }) => width || '100%'};
  max-width: ${({ maxWidth }) => maxWidth || '400px'};
  min-width: ${({ minWidth }) => minWidth || '160px'};

  & .MuiInputBase-root {
    min-height: ${({ height }) => height || '32px'};
    padding: 0 39px 0 0;
  }

  & .MuiInputBase-input.MuiAutocomplete-input {
    min-height: ${({ height }) => height || '32px'};
    padding: 4px 12px;
    box-sizing: border-box;
    font-size: 15px;
    line-height: 1.6;
    color: #444;
  }
`;

export const CustomTextField = styled(TextField, { shouldForwardProp })`
  position: relative;
  width: ${({ width }) => width || '100%'};
  max-width: ${({ maxWidth }) => maxWidth || '400px'};

  & .MuiInputBase-root {
    min-height: ${({ height }) => height || '32px'};
    padding: 0;
  }

  & .MuiInputBase-input {
    min-height: ${({ height }) => height || '32px'};
    padding: ${({ multi }) => multi || '4px 12px'};
    box-sizing: border-box;
    font-size: 15px;
    line-height: 1.6;
    color: #444;

    &::placeholder {
      font-size: 14px;
    }

    &[type='file']::file-selector-button {
      position: absolute;
      top: 2px;
      right: -72px;
      height: 29px;
      padding: 4px 8px;
      background-color: #1976d2;
      font-size: 12px;
      color: #fff;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }

    ${({ right }) =>
      right &&
      `
        text-align: right;
    `}
  }

  & .MuiInputLabel-root {
    line-height: ${({ height }) => height || '32px'};
  }

  & .MuiInputBase-input.Mui-disabled {
    -webkit-text-fill-color: #929292;
    background-color: #efefef;
  }
`;

// ===============================================
// 버튼 그룹 스타일 컴포넌트
// ===============================================
export const CustomButtonGroup = styled(ButtonGroup)`
  margin-bottom: 20px;
  background-color: #fff;

  & .MuiButton-root {
    font-size: 14px;
    padding: 4px 12px;
  }
`;

// ===============================================
// 래아아웃 스타일 컴포넌트
// ===============================================
export const SectionWrapper = styled(Box)`
  padding: 24px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  gap: 16px;

  & + & {
    margin-top: 32px;
  }

  @media print {
    box-shadow: none;
    margin-top: 0;
    padding: 8px;
  }
`;

// ===============================================
// 스택 스타일 컴포넌트
// ===============================================
export const CustomStack = styled(Stack, { shouldForwardProp })`
  display: flex;
  flex-direction: row;
  justify-content: ${({ justifyContent }) => {
    switch (justifyContent) {
      case 'center':
        return 'center';
      case 'end':
        return 'flex-end';
      case 'spaceBetween':
        return 'space-between';
      default:
        return 'flex-start';
    }
  }};
  align-items: center;
  gap: ${({ gap }) => gap || '8px'};
`;

// ===============================================
// 리스트 스타일 컴포넌트
// ===============================================
export const CustomList = styled(List)`
  padding: 0;
  display: flex;
  justify-content: start;
  gap: 8px;
`;

// ===============================================
// 페이지네이션 스타일 컴포넌트
// ===============================================
export const PaginationList = styled(CustomList)`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 24px 0;
`;

export const PaginationItem = styled(ListItem)`
  width: auto;
  padding: 0;
`;

export const PaginationButton = styled(ListItemButton, { shouldForwardProp })`
  min-width: 36px;
  height: 36px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  padding: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #757575;
  font-size: 14px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    color: #424242;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ active }) =>
    active &&
    `
    background-color: #1976d2;
    color: #fff;
    font-weight: 500;
    &:hover {
      background-color: #1565c0;
      color: #fff;
    }
  `}

  & .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

// ===============================================
// 모달 스타일 컴포넌트
// ===============================================
export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    padding: theme.spacing(1),
    minWidth: '320px',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
    textAlign: 'center',
    '& .MuiTableBody-root': {
      '& th': {
        width: '200px',
      },
    },
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1, 2, 2),
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
}));

export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  textAlign: 'center',
  fontSize: '1.25rem',
  fontWeight: 600,
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: 8,
  top: 8,
  color: theme.palette.grey[500],
  '&:hover': {
    color: theme.palette.grey[700],
  },
}));

export const StyledButton = styled(Button)(({ theme, variant }) => ({
  minWidth: '120px',
  margin: '0 !important',
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  borderRadius: '8px',
  ...(variant === 'contained' && {
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  }),
}));

// ===============================================
// 로딩 스타일 컴포넌트
// ===============================================
export const Dim = styled(Container)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  maxWidth: 'unset',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  zIndex: 9999,

  [theme.breakpoints.up('sm')]: {
    width: '100%',
    maxWidth: 'unset',
  },
  [theme.breakpoints.up('md')]: {
    width: '100%',
    maxWidth: 'unset',
  },
  [theme.breakpoints.up('lg')]: {
    width: '100%',
    maxWidth: 'unset',
  },
}));

export const CenterAbsoluteStyle = styled(Stack)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

// ===============================================
// 이미지 스타일 컴포넌트
// ===============================================
export const CustomImg = styled(Box, { shouldForwardProp })`
  width: ${({ width }) => width || '100px'};
  height: ${({ height }) => height || 'auto'};
  object-fit: ${({ objectFit }) => objectFit || 'contain'};
  border-radius: ${({ borderRadius }) => borderRadius || 0};

  ${({ shadow }) =>
    shadow &&
    `
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  `}
`;

export const CustomImgBox = (props) => <CustomImg component="img" {...props} />;

// ===============================================
// 필수입력표시 스타일 컴포넌트
// ===============================================
export const RequiredMarkBox = styled(Box)`
  position: relative;
  padding-right: 8px;
  font-size: 14px;
  color: #444;

  & .MuiBox-root {
    top: 50%;
    left: 7px;
    transform: translateY(-50%);
  }
`;

export const RequiredMark = styled(Box)`
  position: absolute;
  top: calc(50% - 7px);
  left: 14px;
  width: 3px;
  height: 3px;
  background-color: #f55520;
  border-radius: 50%;
`;

export const CustomLink = styled('a', { shouldForwardProp })`
  position: relative;
  text-decoration: ${({ textDecoration }) => textDecoration || 'none'};
  color: ${({ color }) => color || 'inherit'};
  padding-right: ${(props) => props.pr || 0}px;
  cursor: pointer;
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 6px;
    transform: translateY(-50%);
    width: 1px;
    height: 14px;
    background-color: #d9d9d9;
    display: block;
  }

  &:last-child::after {
    display: none;
  }

  &:hover {
    color: ${({ hoverColor }) => hoverColor || '#1976d2'};
    text-decoration: ${({ hoverTextDecoration }) => hoverTextDecoration || 'underline'};
  }
`;

export const CommentListItem = styled(ListItem)`
  display: flex;
  background-color: #f5f5f5;
  padding: 24px 32px;
  border-radius: 8px;
  width: 100%;

  &:first-child {
    margin-top: 24px;
  }

  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;
